<template>
  <v-dialog content-class="my-custom-dialog" :value="value" @input="$emit('input', $event)" max-width="1232" @click:outside="onClose" :persistent="$loader.isAppLoading">
    <v-card title="Dialog" :class="['pa-6', 'background-pattern', 'checkpoint-dialog', { 'storybook-mode': isStorybook }]">
      <div class="mb-9 d-flex align-center  justify-space-between">
        <div class="d-flex align-center">
          <div class="icon-container">
            <img :src="require('@components/assets/pass.svg')" alt="icon">
          </div>
          <div class="text-container">
            <div class="heading">Add Checkpoint</div>
            <div class="description">This will add a checkpoint at the bottom of the corresponding Proficiency Level stack</div>
          </div>
        </div>
       <div class="d-flex align-center">
         <div class="close-icon-container" @click="onClose">
           <v-icon> mdi-close </v-icon>
         </div>
       </div>
      </div>
      <div class="mb-3 add-checkpoint-level-banner-wrap">
        <CheckpointLevelBanner
          :level="bannerLevelName"
          selector-label-suffix=" Level"
          @update:level="onBannerLevelUpdate"
        />
      </div>
      <CriteriaInput
        class="check-criteria-input mb-10"
        :criteria="criteria"
        :criteriaPrefix="criteriaPrefix"
        :dropdownItems="dropdownItems"
        :show-trailing-icon="true"
        @update:criteria="criteria = $event"
        @update:criteriaPrefix="criteriaPrefix = $event"
      />
      <div class="d-flex justify-center ">
        <Button
          size="normal"
          :fullwidth="criteria !== '' ? 'hover' : 'disable'"
          :normal="criteria !== '' ? 'normal-default' : 'normal-disable'"
          label="Save as Draft"
          @onClick="getAllData()"
          :loading="$loader.isAppLoading ? 'left' : null"
        ></Button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import Button from "@components/buttons/Button.vue";
import CheckpointLevelBanner from "@components/CheckpointLevelBanner/CheckpointLevelBanner.vue";
import CriteriaInput from "@components/CriteriaInput/CriteriaInput.vue";

const BANNER_LEVEL_BY_NAME = {
  Apprentice: { id: 1, name: 'Apprentice', value: 'apprentice' },
  Professional: { id: 2, name: 'Professional', value: 'professional' },
  Coach: { id: 3, name: 'Coach', value: 'coach' },
  Master: { id: 4, name: 'Master', value: 'master' },
};

const props = defineProps({
  value: {
    type: Boolean,
    default: false
  },
  levelProp: {
    type: Object,
    default: () => ({
      id: 1,
      name: "Apprentice",
      value: "apprentice",
    }),
  },
  criteriaProp: {
    type: String,
    default: null
  },
  criteriaPrefixProp: {
    type: String,
    default: null
  },
  isStorybook: {
    type: Boolean,
    default: false
  },
});

const emit = defineEmits(['input', 'reset-level', 'onSave']);

const criteria = ref('');
const criteriaPrefix = ref('Can');
const level = ref({
  id: 1,
  name: 'Apprentice',
  value: 'apprentice',
});
const dropdownItems = ref([
  "Can",
  "Does",
  "Demonstrated",
  "Proved"
]);

const bannerLevelName = computed(() => {
  const n = level.value?.name;
  return n && BANNER_LEVEL_BY_NAME[n] ? n : 'Apprentice';
});

function onBannerLevelUpdate(nextName) {
  const mapped = BANNER_LEVEL_BY_NAME[nextName];
  if (mapped) {
    level.value = mapped;
  }
}

onMounted(() => {
  if (props.criteriaProp) {
    criteria.value = props.criteriaProp;
  }
  if (props.criteriaPrefixProp) {
    criteriaPrefix.value = props.criteriaPrefixProp;
  }
  level.value = props.levelProp;
});

watch(() => props.value, (newVal) => {
  if (newVal) {
    criteriaPrefix.value = 'Can';
    criteria.value = "";
    emit('reset-level', level.value);
  }
});

watch(
  () => props.levelProp,
  (newVal) => {
    if (newVal) {
      level.value = newVal;
    }
  },
  { deep: true, immediate: true }
);

watch(
  () => props.criteriaPrefixProp,
  (newVal) => {
    if (newVal) {
      criteriaPrefix.value = newVal;
    }
  },
  { deep: true, immediate: true }
);

const onClose = () => {
  emit('input', false);
};

const getAllData = () => {
  const modalData = {
    criteria: criteria.value,
    prefix: criteriaPrefix.value,
    proficiencyLevel: level.value.id,
  };
  emit('onSave', modalData);

  criteria.value = "";
  emit('reset-level', level.value);
  setTimeout(() => {
    emit("input", false);
  }, 1000);
};

</script>
<style scoped>
/* Apply scoped styles only when NOT in storybook mode */
.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input .criteria-input .v-text-field {
  padding-top: 0px !important;
}

.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input .criteria-input .left-demonstrated-prepand {
  min-width: 132px;
  top: -10px;
}
.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input .criteria-input .left-proved-prepand {
  min-width: 80px;
  top: -10px;
}

.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input {
  overflow: visible !important;
}

.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input .criteria-input .code-select-wrapper {
  overflow: visible !important;
}

.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input .criteria-input .v-menu__content {
  z-index: 9999 !important;
  position: absolute !important;
}

.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input .criteria-input .v-select__content {
  z-index: 9999 !important;
}

.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input .criteria-input .v-list {
  z-index: 9999 !important;
}
.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input.criteria-input.ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -60px !important;
}
.checkpoint-dialog:not(.storybook-mode) ::v-deep .check-criteria-input.criteria-input.ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -10px !important;
}
</style>
<style>
.my-custom-dialog {
  overflow: inherit;
}
.checkpoint-dialog.background-pattern {
  background-image: url("@components/assets/background_pattern_decorative_left.svg");
  background-position: 0 0;
  border-radius: 12px !important;
  box-shadow: 0px 8px 8px -4px #10182808;
}
.checkpoint-dialog .heading {
  color: #101828;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
}
.checkpoint-dialog .description {
  color: #596B89;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px;
  margin-top: -5px;
}
.checkpoint-dialog .icon-container {
  display: flex;
  width: 48px;
  height: 48px;
  padding: 12px;
  justify-content: center;
  align-items: center;
  border-radius:  10px;
  border: 1px solid #EAECF0;
  background: #FFF;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}
.checkpoint-dialog .close-icon-container {
  height: 44px;
  width: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-top: -45px;
  margin-right: -15px;
}
.checkpoint-dialog .text-container {
  padding-left: 20px; 
  width: 100%;
}
.check-criteria-input .main_label {
  display: none;
}
.add-checkpoint-level-banner-wrap {
  margin-top: -10px;
}
</style>
